import { useState, useCallback } from 'react';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { FileAudio, Upload, Trash2 } from 'lucide-react';
import { Track } from '../data/mockData';
import { toast } from 'sonner';
import { useListTracksPaginatedQuery } from '../../store/api';

interface FilesTabProps {
  channelId: string | number;
  stationName: string;
  channelName: string;
  onUploadTrack: (file: File) => Promise<void>;
  onDeleteTrack: (trackId: string | number) => void;
}

export function FilesTab({
  channelId,
  stationName,
  channelName,
  onUploadTrack,
  onDeleteTrack,
}: FilesTabProps) {
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 20;

  const { data: tracksData, isFetching } = useListTracksPaginatedQuery({
    channelId: Number(channelId),
    offset: pageOffset,
    limit: PAGE_LIMIT,
  });

  const tracks: Track[] = (tracksData?.items ?? []).map((t: any) => ({
    ...t,
    id: String(t.id),
    channelId: String(t.channelId ?? t.channel_id),
    fileName: t.fileName ?? t.file_name,
    uploadDate: t.uploadDate ?? t.upload_date,
  }));

  const loadMore = useCallback(() => {
    if (tracksData?.hasMore && !isFetching) {
      setPageOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [tracksData?.hasMore, isFetching]);

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore: tracksData?.hasMore ?? false,
    isFetching,
    onLoadMore: loadMore,
  });

  const [dragActive, setDragActive] = useState(false);
  const [selectedTrackIds, setSelectedTrackIds] = useState<Set<string | number>>(
    new Set()
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'bulk'; track?: Track } | null>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleMultipleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleMultipleFileUpload(Array.from(e.target.files));
    }
    e.target.value = ''; // Reset input
  };

  const handleMultipleFileUpload = async (files: File[]) => {
    const maxSize = 200 * 1024 * 1024;
    const validFiles: File[] = [];

    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.mp3')) {
        toast.error(`"${file.name}" skipped — only MP3 files are allowed`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`"${file.name}" skipped — file size must be less than 200 MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const total = validFiles.length;
    const toastId = 'upload-batch';
    toast.loading(`Uploading 0 / ${total} files...`, { id: toastId });

    let succeeded = 0;
    let failed = 0;

    await Promise.all(
      validFiles.map(async (file) => {
        try {
          await onUploadTrack(file);
          succeeded++;
        } catch {
          failed++;
          toast.error(`"${file.name}" upload failed`);
        }
        toast.loading(`Uploading ${succeeded + failed} / ${total} files...`, { id: toastId });
      })
    );

    if (failed === 0) {
      toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded successfully`, { id: toastId });
    } else {
      toast.warning(`${succeeded} uploaded, ${failed} failed`, { id: toastId });
    }
  };

  const toggleTrackSelection = (trackId: string | number) => {
    setSelectedTrackIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTrackIds.size === tracks.length) {
      setSelectedTrackIds(new Set());
      return;
    }

    setSelectedTrackIds(new Set(tracks.map((track) => track.id)));
  };

  const handleDelete = (track: Track) => {
    setDeleteConfirm({ type: 'single', track });
  };

  const handleDeleteSelected = () => {
    if (selectedTrackIds.size === 0) {
      toast.error('Select at least one file to delete');
      return;
    }
    setDeleteConfirm({ type: 'bulk' });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'single' && deleteConfirm.track) {
      onDeleteTrack(deleteConfirm.track.id);
      toast.success(`${deleteConfirm.track.fileName} deleted`);
    } else if (deleteConfirm.type === 'bulk') {
      selectedTrackIds.forEach((trackId) => onDeleteTrack(trackId));
      setSelectedTrackIds(new Set());
      toast.success('Selected files deleted');
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Files</h2>
          <p className="text-gray-400">Upload and manage MP3 files for your programs</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleDeleteSelected}
            disabled={selectedTrackIds.size === 0}
            className="border-gray-700 text-black hover:text-white hover:bg-gray-800 text-xs sm:text-sm"
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Upload Area */}
      <div className="mb-8">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 sm:p-12 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-950/30'
              : 'border-gray-700 bg-gray-800'
          }`}
        >
          <Upload className="size-12 mx-auto mb-4 text-gray-500" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Drop MP3 files here or click to upload
          </h3>
          <p className="text-sm text-gray-400 mb-4">
            Multiple files supported • Maximum file size: 200 MB • Required: 48 kHz sampling rate, ~128 kbps bitrate
          </p>
          <input
            type="file"
            id="file-upload"
            accept=".mp3"
            multiple
            onChange={handleFileInput}
            className="hidden"
          />
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="mr-2 size-4" />
              Upload MP3s
            </label>
          </Button>
        </div>

        <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
          <p className="text-sm text-yellow-200">
            <strong>Upload Requirements:</strong> MP3 files must have 48 kHz sampling rate
            and approximately 128 kbps average bitrate. Files not meeting these
            requirements will be rejected.
          </p>
        </div>
      </div>

      {/* Tracks Table */}
      {tracks.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <FileAudio className="size-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 text-lg">No files uploaded yet</p>
          <p className="text-gray-500 text-sm mt-2">Upload your first MP3 file to get started</p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400 w-12">
                  <input
                    type="checkbox"
                    checked={tracks.length > 0 && selectedTrackIds.size === tracks.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all files"
                  />
                </TableHead>
                <TableHead className="text-gray-400">File Name</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Size</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Duration</TableHead>
                <TableHead className="text-gray-400 hidden lg:table-cell">Uploaded On</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.map((track) => (
                <TableRow key={track.id} className="border-gray-800 hover:bg-gray-800">
                  <TableCell className="text-gray-400">
                    <input
                      type="checkbox"
                      checked={selectedTrackIds.has(track.id)}
                      onChange={() => toggleTrackSelection(track.id)}
                      aria-label={`Select ${track.fileName}`}
                    />
                  </TableCell>
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      <FileAudio className="size-4 text-gray-500" />
                      {track.fileName}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-400 hidden sm:table-cell">{track.size}</TableCell>
                  <TableCell className="text-gray-400 hidden sm:table-cell">{track.duration}</TableCell>
                  <TableCell className="text-gray-400 hidden lg:table-cell">{track.uploadDate}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(track)}
                        className="text-gray-400 hover:text-red-400 hover:bg-gray-800"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {(tracksData?.hasMore ?? false) && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          <div className="text-sm text-gray-500">Loading more...</div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {deleteConfirm?.type === 'single' && deleteConfirm.track
                ? `Are you sure you want to delete "${deleteConfirm.track.fileName}"? This action uses a soft delete.`
                : `Are you sure you want to delete ${selectedTrackIds.size} selected file(s)? This action uses a soft delete.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}